import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { positionValues } from 'react-custom-scrollbars';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import styled, { css } from 'styled-components';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow-down.svg';
import { ReactComponent as AttachmentIcon } from 'assets/icons/attachment.svg';
import { ReactComponent as DetailsIcon } from 'assets/icons/details.svg';
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg';
import { ReactComponent as NotificationIcon } from 'assets/icons/notifications.svg';
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg';

import {
  INotification,
  NotificationTypeEnums,
} from 'overview/interface/Notification';

import { P } from 'shared/components/Typography';
import { kashmirBlue, silverSandTwo } from 'shared/config/Colors';
import { Spacer } from 'shared/components/Layout';
import { Tooltip } from 'shared/components/Tooltip';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { useGenerateNotifLink } from 'shared/hooks/useGenerateNotifLink';
import { Wrapper } from 'shared/components/Wrapper';
import IconCircle from 'shared/components/IconCircle';
import Loader from 'shared/components/Loader';
import SVG from 'shared/components/SVG';
import ScrollbarsCustom from 'shared/components/ScrollbarsCustom';
import UtilService from 'shared/services/util.service';

import {
  changeReadStatus,
  getRelatedNotifications,
} from './store/notificationsActions';

const RELATED_PAGE_SIZE = 10;
const RELATED_LIST_MAX_HEIGHT = 320;

interface NotificationProps {
  notification: INotification;
  inToast?: boolean;
  isRelated?: boolean;
  showCollaborationOnly: boolean;
  onNotificationClick?: () => void;
}

const NotificationGroup = styled.div`
  border-bottom: 1px solid
    ${({ theme }) => UtilService.colorWithOpacity(theme.mischka, 0.3)};

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationContainer = styled(Wrapper)<{ inToast: boolean }>`
  transition: background-color 300ms ease;
  padding: ${({ inToast }) =>
    !inToast && `16px 5px 16px 20px; cursor: pointer`};

  &:hover {
    background-color: ${({ theme, inToast }) => !inToast && theme.athensGray};
  }
`;

const NotificationBody = styled.div`
  flex: 1;
`;

interface MarkAsReadBtnProps {
  isRead: boolean;
}

const ReadDotStyle = css`
  &::before {
    background-color: ${({ theme }) => theme.kashmirBlue};
    border-radius: 50%;
    content: '';
    height: 8px;
    opacity: 0;
    transition: 300ms ease;
    width: 8px;
  }
`;

const ReadDotStyleActive = css`
  &::before {
    opacity: 1;
  }
`;

const ReadDotStyleHover = css`
  &::before {
    opacity: 0.5;
  }
`;

const MarkAsReadBtn = styled.button<MarkAsReadBtnProps>`
  align-items: center;
  background-color: ${({ theme }) => theme.white};
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.mischka};
  cursor: pointer;
  display: flex;
  height: 20px;
  justify-content: center;
  margin-left: 20px;
  position: relative;
  width: 20px;

  ${ReadDotStyle};

  ${({ isRead }) => !isRead && ReadDotStyleActive}

  &:hover {
    ${({ isRead }) => isRead && ReadDotStyleHover}

    ${Tooltip} {
      opacity: 1;
    }
  }
`;

const RelatedToggle = styled.button`
  align-items: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 0;

  ${P} {
    margin-left: 5px;
  }

  &:hover ${P} {
    text-decoration: underline;
  }

  &.expanded .icon {
    transform: rotate(180deg);
  }
`;

const MarkGroupAsReadBtn = styled.button`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.silverSandTwo};
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;
  padding: 0;
  transition: 300ms ease;

  &:hover {
    color: ${({ theme }) => theme.nevada};
  }
`;

const RelatedList = styled.div`
  background-color: ${({ theme }) => theme.zircon};
  padding: 5px 0 5px 20px;
`;

const MarkAsReadLoaderWrapper = styled.div`
  margin-left: 20px;
`;

const RelatedLoaderWrapper = styled.div`
  .loader {
    justify-content: flex-start;
  }
`;

const Notification: FC<NotificationProps> = ({
  notification,
  inToast,
  isRelated,
  showCollaborationOnly,
  onNotificationClick,
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [relatedLoading, setRelatedLoading] = useState(false);

  const [expanded, setExpanded] = useState(false);

  const [visibleRelatedCount, setVisibleRelatedCount] =
    useState(RELATED_PAGE_SIZE);

  // the notifications panel can be closed while the request is still running
  const isMountedRef = useRef(true);

  const { url, urlState } = useGenerateNotifLink(notification);

  const canExpandRelated =
    !inToast &&
    !isRelated &&
    !!notification.details.orderId &&
    notification.relatedUnreadNotificationsCount > 0;

  const getNotificationIconBasedOnType = () => {
    switch (notification.type) {
      case NotificationTypeEnums.COLLABORATION:
        return <PeopleIcon />;
      case NotificationTypeEnums.ORDER:
        return <FolderIcon />;
      case NotificationTypeEnums.ACK:
        return <DetailsIcon />;
      case NotificationTypeEnums.ATTACHMENTS:
        return <AttachmentIcon />;
      default:
        return <NotificationIcon />;
    }
  };

  const onChangeReadStatusClickHandler = (
    e?: MouseEvent<HTMLButtonElement>
  ) => {
    e?.stopPropagation();

    setLoading(true);
    dispatch(
      changeReadStatus(
        {
          notificationIds: [notification.id],
          read: notification.isRead,
          showCollaborationOnly,
        },
        () => {
          setLoading(false);
          toast.error(
            `Cannot mark notification as ${
              notification.isRead ? 'un' : ''
            }read at this moment.`
          );
        },
        setLoading
      )
    );
  };

  const onMarkGroupAsReadClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    setLoading(true);
    dispatch(
      changeReadStatus(
        {
          notificationIds: [notification.id],
          read: false,
          showCollaborationOnly,
          markAllOrderNotifications: true,
        },
        () => {
          setLoading(false);
          toast.error('Cannot mark notifications as read at this moment.');
        },
        setLoading
      )
    );
  };

  const collapseRelated = () => {
    setExpanded(false);
    setVisibleRelatedCount(RELATED_PAGE_SIZE);
  };

  const onToggleRelatedClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (expanded) {
      collapseRelated();
      return;
    }

    if (notification.relatedNotifications) {
      setExpanded(true);
      return;
    }

    setRelatedLoading(true);

    dispatch(
      getRelatedNotifications(
        {
          notificationId: notification.id,
          orderId: notification.details.orderId!,
        },
        () => isMountedRef.current && setExpanded(true),
        () => {
          if (isMountedRef.current) {
            setRelatedLoading(false);
          }

          toast.error('Cannot load related notifications at this moment.');
        },
        (isLoading) => isMountedRef.current && setRelatedLoading(isLoading)
      )
    );
  };

  const onRelatedScrollUpdateHandler = ({
    scrollTop,
    scrollHeight,
    clientHeight,
  }: positionValues) => {
    if (scrollTop + clientHeight < scrollHeight - 50) return;

    setVisibleRelatedCount((prevState) =>
      Math.min(
        prevState + RELATED_PAGE_SIZE,
        notification.relatedNotifications?.length ?? prevState
      )
    );
  };

  // every related notification got read, so there is nothing left to expand
  useEffect(() => {
    if (!canExpandRelated && expanded) {
      collapseRelated();
    }
  }, [canExpandRelated]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onNotifClickHandler = () => {
    if (!notification.isRead) {
      onChangeReadStatusClickHandler();
    }

    if (url) {
      history.push(url, urlState ?? urlState);
    }

    onNotificationClick?.();
  };

  return (
    <NotificationGroup>
      <NotificationContainer
        flex
        middle
        onClick={!inToast ? onNotifClickHandler : undefined}
        inToast={inToast ?? false}
      >
        <IconCircle icon={getNotificationIconBasedOnType()} size={42} />

        <NotificationBody>
          <P fontSize={12} bold>
            {notification.content}
          </P>
          <Spacer h="5px" />
          <P color={silverSandTwo} fontSize={10} bold>
            {UtilService.formatDate({ date: notification.createdOnUtc })}
          </P>

          {canExpandRelated && (
            <>
              <Spacer h="10px" />

              {relatedLoading ? (
                <RelatedLoaderWrapper>
                  <Loader size={16} noSpacing />
                </RelatedLoaderWrapper>
              ) : (
                <Wrapper flex middle between>
                  <RelatedToggle
                    type="button"
                    className={expanded ? 'expanded' : undefined}
                    onClick={onToggleRelatedClickHandler}
                  >
                    <SVG
                      animateTransform={['transform']}
                      color={kashmirBlue}
                      icon={<ArrowDownIcon />}
                    />

                    <P color={kashmirBlue} fontSize={12} bold>
                      {expanded
                        ? 'Show less'
                        : `View ${notification.relatedUnreadNotificationsCount} more`}
                    </P>
                  </RelatedToggle>

                  <MarkGroupAsReadBtn
                    type="button"
                    onClick={onMarkGroupAsReadClickHandler}
                  >
                    Mark all as Read
                  </MarkGroupAsReadBtn>
                </Wrapper>
              )}
            </>
          )}
        </NotificationBody>

        {loading ? (
          <MarkAsReadLoaderWrapper>
            <Loader size={20} noSpacing />
          </MarkAsReadLoaderWrapper>
        ) : (
          !inToast && (
            <MarkAsReadBtn
              isRead={notification.isRead}
              onClick={onChangeReadStatusClickHandler}
            >
              <Tooltip position="left">
                Mark as {!notification.isRead ? 'Read' : 'Unread'}
              </Tooltip>
            </MarkAsReadBtn>
          )
        )}
      </NotificationContainer>

      {expanded && canExpandRelated && (
        <RelatedList>
          {notification.relatedNotifications?.length ? (
            <ScrollbarsCustom
              autoHeight
              autoHeightMin={0}
              autoHeightMax={RELATED_LIST_MAX_HEIGHT}
              autoHide
              autoHideDuration={300}
              autoHideTimeout={500}
              paddingRight={0}
              onUpdate={onRelatedScrollUpdateHandler}
            >
              {notification.relatedNotifications
                .slice(0, visibleRelatedCount)
                .map((relatedNotification) => (
                  <Notification
                    key={relatedNotification.id}
                    notification={relatedNotification}
                    isRelated
                    showCollaborationOnly={showCollaborationOnly}
                    onNotificationClick={onNotificationClick}
                  />
                ))}
            </ScrollbarsCustom>
          ) : (
            <P color={silverSandTwo} fontSize={10} bold>
              No related notifications.
            </P>
          )}
        </RelatedList>
      )}
    </NotificationGroup>
  );
};

Notification.defaultProps = {
  inToast: false,
  isRelated: false,
};

export { Notification };
